import styled, { keyframes } from "styled-components";

const bp = { md: "768px" };

const c = {
  black: "#1e1e1e",
  gold: "#c9a227",
  pink: "#fc9ac4",
  pinkStrong: "#ff77b4",
  white: "#ffffff",
};

const BAR_H = 56;

/* =======================
   Top bar (mobil)
======================= */

export const Rail = styled.div`
  display: none;
  @media (max-width: ${bp.md}) {
    display: grid;
  }

  width: 100%;
  height: ${BAR_H}px;

  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 14px;

  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.1);

  grid-template-columns: 46px 1fr 46px;
  align-items: center;
  overflow: hidden;
`;

export const Arrow = styled.button`
  height: ${BAR_H}px;
  width: 46px;
  border: 0;
  cursor: pointer;

  background: transparent;
  color: ${c.black};

  font-size: 22px;
  font-weight: 900;

  display: grid;
  place-items: center;

  &:active {
    transform: translateY(1px);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${c.pinkStrong};
    }
  }
`;

export const Scroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Item = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  height: ${BAR_H}px;
  padding: 0 16px;

  border: 0;
  background: transparent;
  cursor: pointer;

  color: rgba(0, 0, 0, 0.78);
  font-weight: 900;

  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-size: 12px;

  position: relative;

  ${({ $active }) =>
    $active &&
    `
    color: ${c.pinkStrong};
  `}

  &::after {
    content: "";
    position: absolute;
    left: 18px;
    right: 18px;
    bottom: 10px;
    height: 4px;
    border-radius: 999px;
    background: ${c.pink};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transform: ${({ $active }) => ($active ? "scaleX(1)" : "scaleX(0.65)")};
    transition: 0.2s ease;
  }

  @media (max-width: ${bp.md}) {
    text-transform: none;
    letter-spacing: 0.04em;
    font-size: 20px;
    font-family: "Cormorant Garamond", serif;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${c.pinkStrong};
    }
  }
`;

/* =======================
   Drawer (mobil) – ca Sidebar desktop
======================= */

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateX(-18px); opacity: 0; }
  to   { transform: translateX(0); opacity: 1; }
`;

export const DrawerOverlay = styled.div`
  @media (min-width: calc(${bp.md} + 1px)) {
    display: none;
  }

  position: fixed;
  inset: 0;

  /* ✅ peste orice header/topbar */
  z-index: 99999;

  background: rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 140ms ease;
`;

export const DrawerPanel = styled.div`
  position: fixed; /* ✅ nu absolute */
  top: 0;
  left: 0;

  height: 100dvh; /* ✅ mai corect pe mobil */
  width: min(88vw, 380px);

  background: ${c.white};
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 18px 0 55px rgba(0, 0, 0, 0.22);

  z-index: 100000; /* ✅ peste overlay */
  animation: ${slideIn} 160ms ease;

  display: flex;
  flex-direction: column;

  /* ✅ iPhone notch safe-area */
  padding-top: env(safe-area-inset-top);
`;

export const DrawerHeader = styled.div`
  padding: 18px 18px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 1;
  white-space: nowrap;

  color: rgba(0, 0, 0, 0.86);

  position: relative;
  padding-bottom: 12px;

  max-width: calc(100% - 52px);
  overflow: hidden;
  text-overflow: ellipsis;

  /* ✅ underline FULL pe titlu */
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0; /* ✅ full width */
    bottom: 2px;
    height: 2px;
    background: ${c.gold};
    opacity: 0.95;
  }
`;

export const DrawerClose = styled.button`
  border: 0;
  cursor: pointer;
  background: transparent;
  color: rgba(0, 0, 0, 0.72);
  font-size: 18px;
  font-weight: 900;

  width: 40px;
  height: 40px;
  border-radius: 12px;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.86);
  }
`;

export const DrawerBody = styled.div`
  padding: 10px 18px 18px;
  overflow: auto;
`;

export const DrawerList = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 12px;
`;

export const DrawerRow = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 14px 6px;
  border-radius: 12px;

  /* ca desktop: fără “caps lock”, aerisit */
  font-family: "Cormorant Garamond", serif;
  font-weight: 600;
  font-size: 22px;
  color: ${({ $active }) => ($active ? c.pinkStrong : "rgba(0,0,0,0.86)")};

  transition:
    background 140ms ease,
    color 140ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
`;

export const DrawerLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const DrawerDot = styled.span`
  width: 7px;
  height: 7px;
  transform: rotate(45deg);
  border-radius: 1px;
  flex: 0 0 auto;

  background: ${c.gold};

  /* ✅ doar la activ */
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: ${({ $active }) =>
    $active ? "rotate(45deg) scale(1)" : "rotate(45deg) scale(0.6)"};

  transition:
    opacity 160ms ease,
    transform 160ms ease;
`;

export const DrawerName = styled.span`
  position: relative;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* underline ca desktop la activ */
  ${({ $active }) =>
    $active &&
    `
    padding-bottom: 6px;
  `}

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 2px;
    height: 3px;
    border-radius: 999px;
    background: ${c.pink};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transform: ${({ $active }) => ($active ? "scaleX(1)" : "scaleX(0.7)")};
    transform-origin: left;
    transition: 180ms ease;
  }
`;

export const DrawerCount = styled.span`
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.55);
  margin-left: 10px;
  flex: 0 0 auto;
`;
